import { useState, useEffect, useLayoutEffect } from 'react';
import { getTeamLogo, getTeamLogoAnySport } from '../utils/getTeamLogo';

// Layout effect on the client (applies the cached logo BEFORE paint, so there's
// no initials→logo flash), plain effect on the server (avoids React's
// useLayoutEffect-on-server warning). Initial render is always null → it matches
// SSR output, so seeding from localStorage never causes a hydration mismatch.
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Module-level cache + in-flight de-duplication for the dynamic team-logo
// lookup. Keyed by `${sport}::${name}`. Survives component remounts so we
// never re-fetch within a session, and is seeded from localStorage on load
// so previously-resolved logos render instantly on repeat visits instead of
// hitting the network every single page load.
const DYNAMIC_LOGO_CACHE = new Map(); // key -> string|null
const DYNAMIC_LOGO_INFLIGHT = new Map(); // key -> Promise<string|null>

// localStorage persistence. A single coarse TTL for the whole blob keeps this
// simple: logos change rarely, and write-through on every fresh resolve means
// an active session keeps the cache warm. The blob fully expires only after
// 24h with no new resolves.
const LS_KEY = 'piks:teamLogoCache:v1';
const LS_TTL_MS = 24 * 60 * 60 * 1000;
const LS_MAX_ENTRIES = 3000; // bound the blob so it can't grow without limit

function seedFromLocalStorage() {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return;
    if (!parsed.t || Date.now() - parsed.t > LS_TTL_MS) return; // stale blob
    const map = parsed.m && typeof parsed.m === 'object' ? parsed.m : {};
    for (const [k, v] of Object.entries(map)) {
      // Persisted nulls are meaningful ("resolved, no logo") — keep them so we
      // don't re-hammer Wikipedia for teams we already know have no crest.
      DYNAMIC_LOGO_CACHE.set(k, v == null ? null : v);
    }
  } catch {
    // Corrupt/oversized storage — ignore and start fresh.
  }
}
seedFromLocalStorage();

let persistTimer = null;
function schedulePersist() {
  if (typeof window === 'undefined' || persistTimer) return;
  persistTimer = setTimeout(() => {
    persistTimer = null;
    try {
      const entries = [...DYNAMIC_LOGO_CACHE.entries()].slice(-LS_MAX_ENTRIES);
      const m = {};
      for (const [k, v] of entries) m[k] = v == null ? null : v;
      window.localStorage.setItem(LS_KEY, JSON.stringify({ t: Date.now(), m }));
    } catch {
      // Quota exceeded / disabled storage — non-fatal.
    }
  }, 500);
}

function dynamicCacheKey(name, sportHints) {
  const sport = (sportHints.find(Boolean) || '').toString().toLowerCase();
  return `${sport}::${String(name).toLowerCase()}`;
}

// --- Request coalescing ------------------------------------------------------
// Instead of firing one GET /api/team-logo per team (which saturates the
// browser's ~6 HTTP/1.1 connections and starves the SSE stream + polls), we
// collect every lookup requested within a ~50ms window and send them together
// to POST /api/team-logos. The server resolves them with bounded concurrency
// and returns url|null in input order.
let pendingBatch = new Map(); // key -> { name, sport }
let pendingResolvers = new Map(); // key -> Array<(url|null) => void>
let flushTimer = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flushBatch, 50);
}

async function flushBatch() {
  flushTimer = null;
  if (pendingBatch.size === 0) return;
  const batch = pendingBatch;
  const resolvers = pendingResolvers;
  pendingBatch = new Map();
  pendingResolvers = new Map();

  const keys = [...batch.keys()];
  const teams = keys.map((k) => batch.get(k));

  // Chunk to the server's per-request cap.
  const CHUNK = 100;
  for (let start = 0; start < keys.length; start += CHUNK) {
    const chunkKeys = keys.slice(start, start + CHUNK);
    const chunkTeams = teams.slice(start, start + CHUNK);
    let results = null;
    try {
      const res = await fetch('/api/team-logos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teams: chunkTeams }),
      });
      if (res.ok) {
        const json = await res.json();
        results = Array.isArray(json?.results) ? json.results : null;
      }
    } catch {
      results = null;
    }
    chunkKeys.forEach((key, i) => {
      const url = results ? (results[i] ?? null) : null;
      // Only cache authoritative answers. On a network/request failure
      // (results === null) resolve null for this paint but DON'T poison the
      // cache — a later mount can retry after the connection recovers.
      if (results) {
        DYNAMIC_LOGO_CACHE.set(key, url);
        schedulePersist();
      }
      DYNAMIC_LOGO_INFLIGHT.delete(key);
      const list = resolvers.get(key) || [];
      list.forEach((fn) => fn(url));
    });
  }
}

function fetchDynamicLogo(name, sportHints) {
  const key = dynamicCacheKey(name, sportHints);
  if (DYNAMIC_LOGO_CACHE.has(key)) {
    return Promise.resolve(DYNAMIC_LOGO_CACHE.get(key));
  }
  if (DYNAMIC_LOGO_INFLIGHT.has(key)) {
    return DYNAMIC_LOGO_INFLIGHT.get(key);
  }
  const sport = sportHints.find(Boolean) || '';
  const p = new Promise((resolve) => {
    if (!pendingResolvers.has(key)) pendingResolvers.set(key, []);
    pendingResolvers.get(key).push(resolve);
    pendingBatch.set(key, { name, sport });
  });
  DYNAMIC_LOGO_INFLIGHT.set(key, p);
  scheduleFlush();
  return p;
}

export function getPickedTeamName(selection, bet) {
  if (!selection || !bet) return null;
  const sel = String(selection).toLowerCase();
  const candidates = [
    bet.awayTeamFull,
    bet.homeTeamFull,
    bet.awayTeam,
    bet.homeTeam,
  ].filter(Boolean);
  for (const name of candidates) {
    const n = String(name).toLowerCase();
    if (n && (sel === n || sel.startsWith(n) || sel.includes(n))) {
      if (n === String(bet.awayTeam || '').toLowerCase() && bet.awayTeamFull) return bet.awayTeamFull;
      if (n === String(bet.homeTeam || '').toLowerCase() && bet.homeTeamFull) return bet.homeTeamFull;
      return name;
    }
  }
  return null;
}

function teamInitials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  return (parts[parts.length - 1] || '').slice(0, 3).toUpperCase();
}

export default function TeamLogo({
  name,
  sport,
  sportName,
  league,
  size = 20,
  accent,
  className = '',
  style: extraStyle,
}) {
  // Try every hint we were given (machine key first, then friendly
  // names like "MLB" / "EUROLEAGUE"), and finally fall back to the
  // any-sport probe so we still surface a logo when the caller can't
  // tell us which league a team belongs to. Without the fallback,
  // games whose `sport` field is missing render bare initials even
  // for major leagues whose teams are all in our maps.
  const hints = [sport, sportName, league].filter(Boolean);
  let staticLogoUrl = null;
  for (const hint of hints) {
    staticLogoUrl = getTeamLogo(name, hint);
    if (staticLogoUrl) break;
  }
  if (!staticLogoUrl) staticLogoUrl = getTeamLogoAnySport(name);

  // Initial state is always null so the first render matches SSR (the module
  // cache — even seeded from localStorage — is client-only). A cache hit is
  // applied in the layout effect below, before paint, so there's no flash.
  const [dynamicLogoUrl, setDynamicLogoUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  const logoUrl = staticLogoUrl || dynamicLogoUrl;

  useEffect(() => {
    setFailed(false);
  }, [logoUrl]);

  // Apply any cached logo synchronously before paint. Running this in a layout
  // effect (not initial useState) keeps the first render hydration-safe while
  // still showing a previously-resolved logo instantly — no initials flash.
  useIsoLayoutEffect(() => {
    if (!name || staticLogoUrl) {
      setDynamicLogoUrl(null);
      return;
    }
    const key = dynamicCacheKey(name, hints);
    setDynamicLogoUrl(DYNAMIC_LOGO_CACHE.has(key) ? DYNAMIC_LOGO_CACHE.get(key) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, staticLogoUrl, sport, sportName, league]);

  // When no hardcoded logo exists and it isn't already cached, ask the
  // server-side resolver (coalesced into a batch). Always apply the resolved
  // value — even null — so a "no logo found" result clears any leftover URL.
  useEffect(() => {
    if (!name || staticLogoUrl) return;
    const key = dynamicCacheKey(name, hints);
    if (DYNAMIC_LOGO_CACHE.has(key)) return; // already applied by the layout effect
    let cancelled = false;
    fetchDynamicLogo(name, hints).then(url => {
      if (!cancelled) setDynamicLogoUrl(url || null);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, staticLogoUrl, sport, sportName, league]);

  const isBadge = !!accent;
  const showLogo = logoUrl && !failed;

  // When a real logo is available we render it on a transparent
  // background so the team's brand colors aren't tinted by our
  // accent fill. The accent color is only used for the initials
  // fallback (so the orange/blue "ND / LSU" placeholder still
  // matches the player side it belongs to).
  const accentColor = accent === 'blue' ? '#3b82f6' : accent === 'orange' ? '#fb923c' : null;
  const fallbackStyles = isBadge && !showLogo && accentColor
    ? {
        background: accent === 'blue' ? 'rgba(59,130,246,0.15)' : 'rgba(251,146,60,0.15)',
        border: `2px solid ${accent === 'blue' ? 'rgba(59,130,246,0.5)' : 'rgba(251,146,60,0.5)'}`,
        color: accentColor,
      }
    : null;

  // Real team logos render transparent (no white puck) so they pop
  // against the black surface and can fill their full slot. A subtle
  // lift + a faint light rim (see `.team-logo-img` in globals.css)
  // keeps even dark/navy marks (e.g. the Padres) legible in dark mode,
  // and a soft drop shadow gives every logo a little depth in light
  // mode. The initials and accent-badge fallbacks keep their original
  // transparent/colored treatment (and stay clipped to a circle).
  const baseStyle = {
    width: size,
    height: size,
    fontSize: Math.max(8, Math.round(size * 0.4)),
    lineHeight: 1,
    ...(fallbackStyles || {}),
    ...(extraStyle || {}),
  };

  // Logos draw transparent + unclipped so their lift/rim isn't cut off;
  // the initials/badge fallbacks keep the round clip + colored fill.
  const containerClass = showLogo
    ? `flex items-center justify-center flex-shrink-0 ${className}`
    : `rounded-full flex items-center justify-center font-black overflow-hidden flex-shrink-0 ${className}`;

  return (
    <div
      className={containerClass}
      style={baseStyle}
      aria-hidden={!name ? 'true' : undefined}
    >
      {showLogo ? (
        <img
          src={logoUrl}
          alt={name || 'Team'}
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="team-logo-img"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : (
        <span style={{ color: isBadge ? undefined : '#9ca3af' }}>{teamInitials(name)}</span>
      )}
    </div>
  );
}

// Fixed-width selection-row logo slot.
// - Resolves the picked team via `getPickedTeamName` and renders a single
//   logo when the selection clearly refers to one side (moneyline/spread).
// - Falls back to a compact dual-logo cluster (away + home, slightly
//   overlapped) for totals/game-level legs and any selection where the
//   picked team cannot be resolved. This keeps the row alignment stable
//   across all bet types.
// Resolve the team a single parlay leg is "about" so we can render its
// logo inside the combined parlay mark.
function legPickName(leg) {
  if (!leg) return null;
  return (
    getPickedTeamName(leg.selectionFull || leg.selection, leg) ||
    leg.homeTeamFull || leg.awayTeamFull ||
    leg.homeTeam || leg.awayTeam ||
    leg.selectionFull || leg.selection ||
    null
  );
}

// Parlay mark — a single circle split into one cell per leg (each cell
// holds that leg's team logo). Signals at a glance that a pick is a
// multi-leg parlay rather than a single team bet.
export function ParlayLogos({ legs, size = 20, sport }) {
  const items = (Array.isArray(legs) ? legs.filter(Boolean) : []).slice(0, 4);
  const n = items.length;
  if (n === 0) {
    return <div className="flex-shrink-0" style={{ width: size, height: size }} aria-hidden="true" />;
  }
  const half = n <= 2;
  const cellW = '50%';
  const cellH = half ? '100%' : '50%';
  const logoSize = Math.round(size * (half ? 0.56 : 0.42));
  return (
    <div
      className="flex-shrink-0 relative rounded-full overflow-hidden flex flex-wrap items-center justify-center"
      style={{
        width: size,
        height: size,
        background: 'rgba(148,163,184,0.10)',
        border: '1.5px solid rgba(148,163,184,0.45)',
      }}
      title={`${n}-leg parlay`}
    >
      {items.map((leg, i) => (
        <div
          key={i}
          className="flex items-center justify-center"
          style={{ width: cellW, height: cellH, boxShadow: 'inset 0 0 0 0.5px rgba(148,163,184,0.4)' }}
        >
          <TeamLogo name={legPickName(leg)} sport={sport || leg?.sport || leg?.sportName} size={logoSize} />
        </div>
      ))}
    </div>
  );
}

export function SelectionLogos({ selection, bet, size = 20, sport }) {
  const resolvedSport = sport || bet?.sport || bet?.sportName;
  const legs = Array.isArray(bet?.legs) ? bet.legs.filter(Boolean) : [];
  const picked = getPickedTeamName(selection, bet);
  // A parlay row can't resolve to a single team — render the combined
  // split-circle mark instead. (The away/home rail calls pass a single
  // clean team name as `selection`, which resolves via `picked`, so they
  // keep their single logo and never hit this branch.)
  const isParlay = legs.length > 1 && (!picked || String(selection || '').includes(','));
  if (isParlay) {
    return <ParlayLogos legs={legs} size={size} sport={resolvedSport} />;
  }
  const slotStyle = { width: picked ? size : Math.round(size * 1.6), height: size };
  if (picked) {
    return (
      <div className="flex-shrink-0 flex items-center justify-center" style={slotStyle}>
        <TeamLogo name={picked} sport={resolvedSport} size={size} />
      </div>
    );
  }
  const away = bet?.awayTeamFull || bet?.awayTeam;
  const home = bet?.homeTeamFull || bet?.homeTeam;
  if (!away && !home) {
    return <div className="flex-shrink-0" style={slotStyle} aria-hidden="true" />;
  }
  return (
    <div className="flex-shrink-0 flex items-center" style={slotStyle}>
      <TeamLogo name={away} sport={resolvedSport} size={size} />
      <div style={{ marginLeft: -Math.round(size * 0.4) }}>
        <TeamLogo name={home} sport={resolvedSport} size={size} />
      </div>
    </div>
  );
}
